import { API_URL } from "../apiConfig";
import {
  formatOrderItemLineLabel,
  isAllSizesVariantLabel,
  shouldShowAllSizesVariant,
  variantLabelForOrderLine,
} from "./variantDisplay";

const SESSION_KEY = "wholesale_catalog_session";

export function wholesaleTokenKey(storeKey) {
  return `wholesale_token:${String(storeKey || "").trim()}`;
}

export function getWholesaleToken(storeKey, catalogSlug) {
  const slug = String(catalogSlug || storeKey || "").trim();
  const key = String(storeKey || "").trim();
  for (const k of [key, slug]) {
    if (!k) continue;
    try {
      const t = localStorage.getItem(wholesaleTokenKey(k));
      if (t) return t;
    } catch {
      /* ignore */
    }
  }
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.token) return parsed.token;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function setWholesaleToken(storeKey, token, catalogSlug) {
  const key = String(storeKey || "").trim();
  const slug = String(catalogSlug || storeKey || "").trim();
  try {
    if (token) {
      if (key) localStorage.setItem(wholesaleTokenKey(key), token);
      if (slug && slug !== key) localStorage.setItem(wholesaleTokenKey(slug), token);
      localStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ token, storeKey: key, catalogSlug: slug, ts: Date.now() })
      );
    } else {
      if (key) localStorage.removeItem(wholesaleTokenKey(key));
      if (slug) localStorage.removeItem(wholesaleTokenKey(slug));
      localStorage.removeItem(SESSION_KEY);
    }
  } catch {
    /* ignore */
  }
}

const DEVICE_ID_KEY = "wholesale_device_id";

/** Stable per-browser id; shared dealer accounts use it to keep carts/orders per device. */
export function getWholesaleDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (id && /^[A-Za-z0-9_-]{8,64}$/.test(id)) return id;
    id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID().replace(/-/g, "")
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return "";
  }
}

export function wholesaleAuthHeaders(storeKey, extraHeaders = {}, catalogSlug) {
  const token = getWholesaleToken(storeKey, catalogSlug);
  if (!token) return { ...extraHeaders };
  const deviceId = getWholesaleDeviceId();
  return {
    ...extraHeaders,
    Authorization: `Bearer ${token}`,
    ...(deviceId ? { "X-Wholesale-Device": deviceId } : {}),
  };
}

export async function wholesaleCatalogFetch(storeKey, path, options = {}, catalogSlug) {
  const slug = String(storeKey || "").trim();
  const headers = wholesaleAuthHeaders(slug, options.headers || {}, catalogSlug);
  const url = `${API_URL}/catalog/${encodeURIComponent(slug)}${path}`;
  return fetch(url, { ...options, headers });
}

export function buildWholesaleDealerLoginUrl(ownerCatalogSlug, dealerUsername) {
  const slug = String(ownerCatalogSlug || "").trim();
  const username = String(dealerUsername || "").trim();
  if (!slug || !username || typeof window === "undefined") return "";
  const base = `${window.location.origin}/catalog/${encodeURIComponent(slug)}`;
  const qs = new URLSearchParams({ ws: "1", username });
  return `${base}?${qs.toString()}`;
}

export function parseWholesaleLoginFromSearch(searchValue) {
  if (typeof URLSearchParams === "undefined") return { open: false, username: "" };
  const params =
    searchValue instanceof URLSearchParams
      ? searchValue
      : new URLSearchParams(String(searchValue || ""));
  const username = String(params.get("username") || "").trim();
  const open =
    params.get("ws") === "1" || params.get("ws_login") === "1" || Boolean(username);
  return { open, username };
}

export function getWholesalePaymentLogEntries(order) {
  return Array.isArray(order?.payment_log)
    ? order.payment_log.filter((e) => e && typeof e === "object")
    : [];
}

export function formatWholesalePaymentMethod(method, language = "ar") {
  const m = String(method || "").trim().toLowerCase();
  if (m === "cash") return language === "ar" ? "كاش" : "Cash";
  if (m === "checks") return language === "ar" ? "شيكات" : "Checks";
  return "";
}

const PAYMENT_LOG_MONEY_ACTIONS = new Set([
  "partial_payment",
  "marked_paid",
  "confirmed_paid",
]);

/** Human-readable payment breakdown for owner stats / dealer order views. */
export function summarizeWholesalePaymentsForDisplay(order, language = "ar") {
  const ar = language === "ar";
  const entries = getWholesalePaymentLogEntries(order).filter((e) =>
    PAYMENT_LOG_MONEY_ACTIONS.has(String(e?.action || ""))
  );
  if (!entries.length) return null;

  const parts = [];
  for (const entry of entries) {
    const amt = Number(entry.amount);
    if (!Number.isFinite(amt) || amt <= 0) continue;
    const method = formatWholesalePaymentMethod(entry.payment_method, language);
    if (ar) {
      parts.push(method ? `₪${amt.toFixed(2)} ${method}` : `₪${amt.toFixed(2)}`);
    } else {
      parts.push(method ? `₪${amt.toFixed(2)} (${method})` : `₪${amt.toFixed(2)}`);
    }
  }
  if (!parts.length) return null;

  const remaining = wholesaleOrderAmountRemaining(order);
  if (ar) {
    const paidLine = `دفع ${parts.join("، ")}`;
    return remaining > 0.009 ? `${paidLine} — المتبقي ₪${remaining.toFixed(2)}` : paidLine;
  }
  const paidLine = `Paid ${parts.join(", ")}`;
  return remaining > 0.009 ? `${paidLine} — remaining ₪${remaining.toFixed(2)}` : paidLine;
}

/** Dealer-facing payment log — hides owner-only reasons (e.g. mark-unpaid note). */
export function formatPaymentLogEntryForDealer(entry, language = "ar") {
  if (!entry || typeof entry !== "object") return "—";
  if (entry.action === "marked_unpaid") {
    const at = entry.at;
    let ts = "";
    if (at) {
      try {
        ts = new Date(at).toLocaleString(language === "ar" ? "ar-EG" : "en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        ts = "";
      }
    }
    return language === "ar"
      ? ts
        ? `تم تغيير حالة الطلبية من مدفوعة إلى غير مدفوعة (${ts})`
        : "تم تغيير حالة الطلبية من مدفوعة إلى غير مدفوعة"
      : ts
        ? `Order changed from paid to unpaid (${ts})`
        : "Order changed from paid to unpaid";
  }
  return language === "ar" ? entry.message_ar : entry.message_en;
}

export function sanitizePaymentLogForDealer(log) {
  const items = Array.isArray(log) ? log : [];
  return items.map((entry) => {
    if (!entry || typeof entry !== "object" || entry.action !== "marked_unpaid") {
      return entry;
    }
    const { reason, ...rest } = entry;
    return {
      ...rest,
      message_ar: formatPaymentLogEntryForDealer(entry, "ar"),
      message_en: formatPaymentLogEntryForDealer(entry, "en"),
    };
  });
}

export function wholesaleOrderTotal(order) {
  return Number(order?.total_amount || 0);
}

export function wholesaleOrderAmountPaid(order) {
  if (order?.amount_paid != null && order.amount_paid !== "") {
    const n = Number(order.amount_paid);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  if (order?.is_paid) return wholesaleOrderTotal(order);
  let partial = 0;
  for (const entry of getWholesalePaymentLogEntries(order)) {
    if (entry.action === "partial_payment") {
      partial += Number(entry.amount || 0);
    }
  }
  return partial > 0 ? partial : 0;
}

export function wholesaleOrderAmountRemaining(order) {
  if (order?.amount_remaining != null && order.amount_remaining !== "") {
    const n = Number(order.amount_remaining);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  if (order?.is_paid) return 0;
  return Math.max(0, wholesaleOrderTotal(order) - wholesaleOrderAmountPaid(order));
}

/** Payment badge label for catalog / dealer views. */
export function getWholesalePaymentStatus(order, language = "ar") {
  if (order?.is_paid) {
    return { label: language === "ar" ? "مدفوعة" : "Paid", tone: "confirmed" };
  }
  const paid = wholesaleOrderAmountPaid(order);
  const remaining = wholesaleOrderAmountRemaining(order);
  if (paid > 0.009 && remaining > 0.009) {
    return {
      label: language === "ar" ? "غير مدفوعة بالكامل" : "Not fully paid",
      tone: "partial",
    };
  }
  return {
    label: language === "ar" ? "غير مدفوعة" : "Unpaid",
    tone: "unpaid",
  };
}

export function formatWholesaleOrderItemLine(it) {
  const sizeTxt = String(it?.selected_size || "").trim();
  const colorTxt = String(it?.selected_color || "").trim();
  const variantTxt = String(it?.selected_variant || "").trim();
  const baseName =
    String(it?.product_name_ar_snapshot || it?.product_name_snapshot || "").trim() ||
    "منتج";
  const name = formatOrderItemLineLabel({
    productName: baseName,
    selectedVariant: variantTxt,
    selectedSize: sizeTxt,
    selectedColor: colorTxt,
  });
  const qty = Number(it?.quantity || 1);
  const unit = Number(it?.unit_price || 0);
  const parts = [name];
  if (
    variantTxt &&
    variantTxt !== "الافتراضي" &&
    (!isAllSizesVariantLabel(variantTxt) ||
      shouldShowAllSizesVariant(variantTxt, sizeTxt, colorTxt))
  ) {
    const shown = variantLabelForOrderLine(variantTxt, sizeTxt, colorTxt);
    if (shown && !name.endsWith(shown)) parts.push(shown);
  }
  if (sizeTxt) parts.push(`مقاس: ${sizeTxt}`);
  if (colorTxt) parts.push(`لون: ${colorTxt}`);
  if (it?.note) parts.push(`ملاحظة: ${it.note}`);
  return { label: parts.join(" · "), qty, unit, total: unit * qty };
}

function wholesalePaymentLog(order) {
  return Array.isArray(order?.payment_log)
    ? order.payment_log.filter((e) => e && typeof e === "object")
    : [];
}

function parseEditTs(iso) {
  if (!iso) return NaN;
  try {
    return new Date(iso).getTime();
  } catch {
    return NaN;
  }
}

function nameFromEditMessage(msg) {
  const m = String(msg || "").match(/من قبل (.+?) \(/);
  return m?.[1] ? m[1].trim() : "";
}

/** Last dealer edit only (never treat store-owner log lines as dealer edits). */
export function getLastDealerEditEntry(order) {
  const log = wholesalePaymentLog(order);
  for (let i = log.length - 1; i >= 0; i -= 1) {
    if (log[i].action === "dealer_edited_items") return log[i];
  }
  for (let i = log.length - 1; i >= 0; i -= 1) {
    const e = log[i];
    if (e.action === "owner_edited_items") continue;
    const msg = String(e.message_ar || e.message_en || "");
    if (msg.includes("صاحب المتجر")) continue;
    if (!e.action && msg.includes("تعديل")) return e;
  }
  const dealerCol = order?.dealer_modified_at;
  if (!dealerCol) return null;
  const ownerEntry = getLastOwnerEditEntry(order);
  if (!ownerEntry) return { at: dealerCol, action: "dealer_edited_items" };
  const dealerTs = parseEditTs(dealerCol);
  const ownerTs = parseEditTs(ownerEntry.at || order?.owner_modified_at);
  if (Number.isFinite(dealerTs) && Number.isFinite(ownerTs) && dealerTs > ownerTs) {
    return { at: dealerCol, action: "dealer_edited_items" };
  }
  if (Number.isFinite(dealerTs) && !Number.isFinite(ownerTs)) {
    return { at: dealerCol, action: "dealer_edited_items" };
  }
  const hasDealerLog = log.some((e) => e.action === "dealer_edited_items");
  return hasDealerLog ? { at: dealerCol, action: "dealer_edited_items" } : null;
}

/** Last store-owner edit only. */
export function getLastOwnerEditEntry(order) {
  const log = wholesalePaymentLog(order);
  for (let i = log.length - 1; i >= 0; i -= 1) {
    if (log[i].action === "owner_edited_items") return log[i];
  }
  for (let i = log.length - 1; i >= 0; i -= 1) {
    const msg = String(log[i].message_ar || log[i].message_en || "");
    if (msg.includes("صاحب المتجر") && msg.includes("تعديل")) return log[i];
  }
  if (order?.owner_modified_at) {
    return { at: order.owner_modified_at, action: "owner_edited_items" };
  }
  return null;
}

export function getDealerModifiedTimestamp(order) {
  const entry = getLastDealerEditEntry(order);
  return entry?.at ? String(entry.at) : null;
}

export function getOwnerModifiedTimestamp(order) {
  const entry = getLastOwnerEditEntry(order);
  return entry?.at ? String(entry.at) : null;
}

export function getOwnerModifierName(order, fallbackStoreName = "") {
  const entry = getLastOwnerEditEntry(order);
  if (!entry) return "";
  if (entry.actor_name) return String(entry.actor_name).trim();
  const fromMsg = nameFromEditMessage(entry.message_ar || entry.message_en);
  if (fromMsg && fromMsg !== "صاحب المتجر") return fromMsg;
  const fromRow =
    order?.owner_modifier_name ||
    order?.owner_store_name ||
    String(fallbackStoreName || "").trim();
  return String(fromRow || "").trim();
}

export function getDealerModifierName(order, fallbackDealerName = "") {
  const entry = getLastDealerEditEntry(order);
  if (!entry) return "";
  if (entry.actor_name) return String(entry.actor_name).trim();
  const fromMsg = nameFromEditMessage(entry.message_ar || entry.message_en);
  if (fromMsg && fromMsg !== "التاجر") return fromMsg;
  const fromRow =
    order?.dealer_modifier_name ||
    order?.dealer?.name ||
    String(fallbackDealerName || "").trim();
  return String(fromRow || "").trim();
}

export function orderIsDealerModified(order) {
  return Boolean(getLastDealerEditEntry(order));
}

export function orderIsOwnerModified(order) {
  return Boolean(getLastOwnerEditEntry(order));
}

export function orderIsModified(order) {
  return Boolean(
    order?.is_order_modified ||
      orderIsDealerModified(order) ||
      orderIsOwnerModified(order)
  );
}

function normVariantLabel(value) {
  return String(value ?? "")
    .trim()
    .replace(/[\u200c\u200f\u202a-\u202e]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Wholesale unit price for a variant label (matches backend logic). */
export function wholesaleUnitPrice(product, prices, selectedVariant, selectedSize) {
  let wp = product?.wholesale_price;
  if (wp == null || wp === "") {
    const bu = product?.baseUnitPrice;
    if (bu != null && bu !== "" && Number.isFinite(Number(bu))) {
      return Math.round(Number(bu) * 100) / 100;
    }
    return null;
  }
  const wholesaleBase = Number(wp);
  if (!Number.isFinite(wholesaleBase)) return null;
  const retailBase = Number(product?.retail_price ?? product?.price ?? 0);

  const labelsToTry = [];
  const push = (raw) => {
    const label = normVariantLabel(raw);
    if (label && !labelsToTry.includes(label)) labelsToTry.push(label);
  };
  push(selectedVariant);
  const fromSize = resolveVariantLabelForSize(product, selectedSize);
  if (fromSize) push(fromSize);
  push(selectedSize);

  const list = Array.isArray(prices) ? prices : [];
  for (const variant of labelsToTry) {
    if (!variant || variant === "الافتراضي") {
      return Math.round(wholesaleBase * 100) / 100;
    }
    for (const p of list) {
      const label = normVariantLabel(p.description_ar || p.description_en || "");
      if (label === variant || label.startsWith(`${variant} - `)) {
        const epRetail = Number(p.price || 0);
        if (Number.isFinite(retailBase) && retailBase > 0) {
          return Math.round((wholesaleBase + (epRetail - retailBase)) * 100) / 100;
        }
        return Math.round(wholesaleBase * 100) / 100;
      }
    }
  }

  return Math.round(wholesaleBase * 100) / 100;
}

/** Unit price for cart checkout validation (dealer session). */
export function cartItemWholesaleUnitPrice(item) {
  const prices = item?.extra_prices || item?.prices || [];
  const variant =
    item?.selectedVariant ||
    resolveVariantLabelForSize(item, item?.selectedSize) ||
    null;
  return wholesaleUnitPrice(item, prices, variant, item?.selectedSize);
}

export function resolveVariantLabelForSize(product, size) {
  const sz = String(size ?? "").trim();
  if (!sz) return "الافتراضي";
  const prices = Array.isArray(product?.prices) ? product.prices : [];
  const hit = prices.find((p) => {
    const label = String(p.description_ar || p.description_en || "").trim();
    return label === sz || label.startsWith(`${sz} - `);
  });
  if (hit) return String(hit.description_ar || hit.description_en || "").trim();
  const allSizes = prices.find((p) => {
    const label = String(p.description_ar || p.description_en || "").trim();
    return label === "جميع الأحجام" || label.startsWith("جميع الأحجام - ");
  });
  if (allSizes) return String(allSizes.description_ar || allSizes.description_en || "").trim();
  return sz;
}

export function formatWholesaleDateTime(iso, language = "ar") {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(language === "ar" ? "ar-EG" : "en-GB", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

/** Dates/times with Western digits (catalog dealer orders modal). */
/** Full sentence: store owner edited this order (AR/EN). */
export function ownerModifiedOrderMessage(iso, language = "ar", storeName = "") {
  if (!iso) return "";
  const dt = formatWholesaleDateTime(iso, language);
  const who =
    String(storeName || "").trim() ||
    (language === "ar" ? "المتجر" : "the store");
  return language === "ar"
    ? `تم تعديل هذه الطلبية من قبل ${who} — ${dt}`
    : `This order was edited by ${who} — ${dt}`;
}

export function dealerModifiedOrderMessage(iso, language = "ar", dealerName = "") {
  if (!iso) return "";
  const dt = formatWholesaleDateTime(iso, language);
  const who =
    String(dealerName || "").trim() || (language === "ar" ? "التاجر" : "the dealer");
  return language === "ar"
    ? `تم تعديل هذه الطلبية من قبل ${who} — ${dt}`
    : `This order was edited by ${who} — ${dt}`;
}

export function formatDealerOrderDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}
